import type { RegisterOptions } from "react-hook-form";
import { emailValidationRegex } from "@/helper/regex";
import { validateYouTubeUrl } from "@/helper/youTubePlayerUtils";
import {
  isEmailField,
  TRIGGER_CLASS_NAME_FIELD_NAME,
  YOUTUBE_URL_FIELD_NAME,
} from "@/components/tables/fields/fieldNameRules";
import {
  maxLengthRule,
  patternRule,
  requiredRule,
  type FieldRuleSource,
} from "@/components/tables/fields/fieldRules";

type Translate = (key: string) => string;

export interface TextFieldRuleSource extends FieldRuleSource {
  id?: string;
}

export interface EmailFieldState {
  generatedForAnalogUser: boolean;
  lockedForRegisteredUser: boolean;
}

export interface TextFieldRuleContext {
  email: EmailFieldState;
  triggerClassNameRequired: boolean;
  modalMode?: string;
  userEmails?: string[];
}

export const getEmailFieldState = (
  fieldName: string | undefined,
  {
    entityOfTable,
    analogFlag,
    subdomain,
    modalMode,
    isRegistered,
  }: {
    entityOfTable?: string;
    analogFlag: unknown;
    subdomain: string;
    modalMode?: string;
    isRegistered: unknown;
  }
): EmailFieldState => {
  if (!isEmailField(fieldName)) {
    return { generatedForAnalogUser: false, lockedForRegisteredUser: false };
  }
  return {
    generatedForAnalogUser:
      entityOfTable === "user" &&
      (analogFlag === true || analogFlag === "true"),
    lockedForRegisteredUser:
      subdomain === "manage" && modalMode === "edit" && isRegistered === true,
  };
};

// only rules are scheduled; everything else (and an unscheduled rule) needs a trigger class
export const isTriggerClassNameRequired = (
  entityOfTable: string | undefined,
  scheduledFlag: unknown
) =>
  entityOfTable !== "rule" ||
  scheduledFlag === "false" ||
  scheduledFlag == null ||
  scheduledFlag === false;

const isTextFieldRequired = (
  field: TextFieldRuleSource,
  ctx: TextFieldRuleContext
) => {
  if (isEmailField(field.id)) return !ctx.email.generatedForAnalogUser; // preserved quirk: email is required even when the column says otherwise
  if (field.id === TRIGGER_CLASS_NAME_FIELD_NAME) {
    return ctx.triggerClassNameRequired;
  }
  return field.required;
};

export const buildTextFieldRules = (
  field: TextFieldRuleSource,
  ctx: TextFieldRuleContext,
  t: Translate
): RegisterOptions => ({
  ...patternRule(field, t),
  ...requiredRule(
    isTextFieldRequired(field, ctx),
    field.errorMessage || t("field_required")
  ),
  ...maxLengthRule(field),
  validate: {
    value: (value: any) => {
      if (isEmailField(field.id)) {
        if (ctx.email.generatedForAnalogUser) return true;
        if (
          ctx.modalMode === "insert" &&
          field.validate &&
          ctx.userEmails?.includes(value)
        ) {
          return field.errorMessageValidation;
        }
        if (!emailValidationRegex.test(value)) return t("enter_valid_email");
      }
      if (field.id === YOUTUBE_URL_FIELD_NAME && !validateYouTubeUrl(value)) {
        return t("youtube_invalid_url_error");
      }
      return true;
    },
  },
});
